import React from 'react';

/*
 * Status chip, route mark, and count badge.
 *
 * A chip shows lifecycle or trust state, never a decorative category. The
 * tones are semantic and soft: a coloured block behind small text is harder to
 * read and turns state into noise. Private is purple, not red, because private
 * is a boundary and not a failure.
 *
 * Shape carries meaning here, and the design audit is explicit about which is
 * which: a status is a 24px block on a 6px radius, a count is a 21px pill on a
 * full radius. A state is a label you read; a count is a number you scan, and
 * giving them the same outline makes the second look like the first.
 */

export type ChipTone = 'neutral' | 'ok' | 'run' | 'warn' | 'err' | 'private';

const TONE: Record<ChipTone, string> = {
  neutral: 'bg-canvas-inset text-ink-muted',
  ok: 'bg-ok-soft text-[#3F7A2F]',
  run: 'bg-canvas-inset text-ink',
  warn: 'bg-[rgba(255,234,0,0.2)] text-[#8A6D00]',
  err: 'bg-danger-soft text-danger',
  private: 'bg-grape-soft text-[#6F3AA3]',
};

const DOT: Record<ChipTone, string> = {
  neutral: 'bg-ink-soft',
  ok: 'bg-ok',
  run: 'bg-ink',
  warn: 'bg-[#D6C400]',
  err: 'bg-danger',
  private: 'bg-grape',
};

export const Chip: React.FC<{
  tone?: ChipTone;
  dot?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ tone = 'neutral', dot = false, className = '', children }) => (
  <span className={`inline-flex h-6 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[11.5px] font-semibold leading-none ${TONE[tone]} ${className}`}>
    {dot && <span className={`h-1.5 w-1.5 rounded-full ${DOT[tone]}`} />}
    {children}
  </span>
);

/*
 * The lifecycle vocabulary in one place. A screen names a state; it never
 * picks a colour. An unmapped state is shown as itself rather than dropped:
 * an unlabelled state is still information, and hiding it makes the interface
 * lie by omission.
 */
const STATUS: Record<string, { label: string; tone: ChipTone }> = {
  ready: { label: 'Ready to use', tone: 'neutral' },
  ready_short: { label: 'Ready', tone: 'neutral' },
  reusable: { label: 'Reusable', tone: 'neutral' },
  run_now: { label: 'Run now', tone: 'neutral' },
  in_delivery: { label: 'In delivery', tone: 'run' },
  in_progress: { label: 'In progress', tone: 'run' },
  active: { label: 'Active', tone: 'ok' },
  confirmed: { label: 'Confirmed', tone: 'neutral' },
  learning: { label: 'Learning', tone: 'neutral' },
  pending: { label: 'Pending', tone: 'warn' },
  needs_owner: { label: 'Needs an owner', tone: 'warn' },
  needs_review: { label: 'Needs review', tone: 'warn' },
  blocked: { label: 'Blocked', tone: 'err' },
  declined: { label: 'Declined', tone: 'err' },
  trusted: { label: 'Trusted', tone: 'ok' },
  record_only: { label: 'Record only', tone: 'neutral' },
  private: { label: 'Private', tone: 'private' },
  paused: { label: 'Paused', tone: 'neutral' },
  retired: { label: 'Retired', tone: 'neutral' },
};

export const StatusChip: React.FC<{ status: string; label?: string; className?: string }> = ({
  status, label, className = '',
}) => {
  const known = STATUS[status];
  if (!known) return <Chip className={className}>{label || status}</Chip>;
  return <Chip tone={known.tone} className={className}>{label || known.label}</Chip>;
};

/*
 * Route mark. A rounded tile carrying one bold letter on a soft semantic
 * ground: Q for Quick Win, P for Project, A for Adoption, D for a connection
 * or data source. 37px in a list row, 46px in a banner.
 */
export type RouteKind = 'quick_win' | 'project' | 'adoption' | 'connection' | 'neutral';

const MARK: Record<RouteKind, { fill: string; glyph: string }> = {
  quick_win: { fill: 'bg-canvas-inset text-ink-muted', glyph: 'Q' },
  project: { fill: 'bg-ink text-white', glyph: 'P' },
  adoption: { fill: 'bg-ok-soft text-[#3F7A2F]', glyph: 'A' },
  connection: { fill: 'bg-grape-soft text-[#6F3AA3]', glyph: 'D' },
  neutral: { fill: 'bg-canvas-inset text-ink-muted', glyph: '' },
};

const MARK_SIZE: Record<string, string> = {
  sm: 'h-[30px] w-[30px] rounded-lg text-[11px]',
  md: 'h-[34px] w-[34px] rounded-tile text-[12px]',
  lg: 'h-[37px] w-[37px] rounded-tile text-[13px]',
  xl: 'h-[46px] w-[46px] rounded-tile text-[15px]',
};

export const RouteMark: React.FC<{
  kind?: RouteKind;
  glyph?: string;
  icon?: React.ReactNode;
  size?: keyof typeof MARK_SIZE;
  className?: string;
}> = ({ kind = 'neutral', glyph, icon, size = 'md', className = '' }) => {
  const mark = MARK[kind];
  return (
    <span
      aria-hidden
      className={`inline-flex shrink-0 items-center justify-center font-sans font-bold ${MARK_SIZE[size]} ${mark.fill} ${className}`}
    >
      {icon}
      {!icon && (glyph || mark.glyph)}
    </span>
  );
};

/** Sidebar and tab counts. Data, so tabular figures, and a pill. */
export const CountBadge: React.FC<{ value: number; onDark?: boolean; className?: string }> = ({
  value, onDark = false, className = '',
}) => {
  if (!value) return null;
  const tone = {
    true: 'bg-white/[0.12] text-white/70',
    false: 'bg-canvas-inset text-ink-muted',
  }[String(onDark)];
  return (
    <span className={`inline-flex h-[21px] items-center rounded-full px-2 font-sans text-[11px] leading-none tabular-nums ${tone} ${className}`}>
      {value}
    </span>
  );
};
